import {
  ComplianceCaseAction as CaseActions,
  ComplianceCaseTypeEnum as CaseTypeEnum,
} from "~/graphql/supergraphEnums";
import {
  type ComplianceCaseStatus,
  type ComplianceCaseSubStatus,
  type ComplianceCaseTypeEnum,
  type GetComplianceCaseQuery,
} from "~/graphql/supergraphTypes";
import { isInternalEscalationSource } from "../../../utils/constants";

export type EscalationType = NonNullable<
  NonNullable<GetComplianceCaseQuery["complianceCase"]>["escalations"]
>[0];

export const isEscalationType = (value: unknown): value is EscalationType => {
  if (!value || typeof value !== "object") return false;
  const obj = value as { id?: unknown };
  return typeof obj.id === "number";
};

export const ACTION_STATUS_SUBSTATUS_LABELS_MAP = {
  resolved: "Confirm case resolved",
  approved: "Confirm resolution is approved",
  rejected: "Confirm resolution is rejected",
  return_to_maker: "Return to maker",
  confirm: {
    under_review: {
      proposed_resolved: "Confirm no true matches",
      proposed_escalation: "Confirm GP escalation",
      proposed_invalid: "Confirm case invalid",
      proposed_deficient: "Confirm LP data/doc collection",
      proposed_acceptance: "Confirm verdict: GP approved",
      proposed_rejection: "Confirm verdict: GP rejected",
      proposed_approved: "Confirm case approved",
      proposed_denied: "Confirm case denied",
      proposed_waived: "Confirm GP waived compliance",
      escalated: "Confirm GP escalation",
    },
  },
  waive: {
    under_review: {
      proposed_waived: "Confirm GP waived compliance",
    },
  },
  confirmed: {
    "": "Confirm",
    under_review: {
      proposed_escalation: "Confirm escalation",
      proposed_resolved: "Confirm case resolved",
      proposed_acceptance: "Confirm payment accepted",
      proposed_invalid: "Confirm case invalid",
      proposed_rejection: "Confirm payment rejected",
      cleared: "Confirm CO verdict: resolved",
      halted: "Confirm CO verdict: halted",
      resolved: "Confirm case resolved",
    },
  },
  proceed: {
    under_review: {
      "": "Confirm case approved",
      accepted: "Confirm verdict: GP approved",
      rejected: "Confirm verdict: GP rejected",
      proposed_waived: "Confirm GP waived compliance",
      proposed_deferred: "Confirm deferral",
    },
  },
  escalate: {
    under_review: {
      proposed_fund_escalation: "Confirm GP escalation & assign to self",
    },
  },
  invalidate: {
    under_review: {
      proposed_deficient: "Case marked invalid",
      proposed_invalid: "Case marked invalid",
    },
  },
  mark_deficient: {
    under_review: {
      proposed_deficient: "Confirm LP re-submission request & assign to self",
    },
  },
} as const;

const INTERNAL_SOURCE_CONFIRM_LABELS: Partial<Record<string, string>> = {
  proposed_escalation: "Confirm escalation",
};

const INTERNAL_ESCALATION_CONFIRM_LABELS: Partial<Record<string, string>> = {
  proposed_acceptance: "Confirm approved by Internal",
  proposed_rejection: "Confirm rejected by Internal",
};

/**
 * Internal-escalation wording (entity/client onboarding subject) for the checker: confirming the
 * onboarding case's escalation, and confirming or returning its escalation children's verdicts.
 * Undefined means keep the default label.
 */
export const getInternalLabel = (
  action: string,
  caseSubstatus: ComplianceCaseSubStatus | null | undefined,
  caseType: ComplianceCaseTypeEnum | null | undefined,
  sourceCaseType: ComplianceCaseTypeEnum | null | undefined,
): string | undefined => {
  if (isInternalEscalationSource(caseType)) {
    return action === CaseActions.Confirm && caseSubstatus
      ? INTERNAL_SOURCE_CONFIRM_LABELS[caseSubstatus]
      : undefined;
  }
  if (caseType !== CaseTypeEnum.Escalation || !isInternalEscalationSource(sourceCaseType)) {
    return undefined;
  }
  if (action === CaseActions.ReturnToMaker) return "Return";
  return action === CaseActions.Confirm && caseSubstatus
    ? INTERNAL_ESCALATION_CONFIRM_LABELS[caseSubstatus]
    : undefined;
};

const CONFIRMED_LABELS_MAP: Map<ComplianceCaseSubStatus, string> = new Map([
  ["proposed_resolved", "Resolved"],
  ["proposed_invalid", "Invalid"],
  ["proposed_escalation", "Escalated"],
  ["cleared", "CO verdict: resolved"],
  ["halted", "CO verdict: halt"],
  ["proposed_acceptance", "Accepted"],
  ["proposed_rejection", "Rejected"],
  ["resolved", "Resolved"],
  ["proposed_deferred", "Deferred"],
]);

export const getLabel = (
  key: string,
  caseSubstatus?: ComplianceCaseSubStatus | null,
  status?: ComplianceCaseStatus | null,
): string => {
  const label = (ACTION_STATUS_SUBSTATUS_LABELS_MAP as Record<string, unknown>)[key];
  if (typeof label === "string") {
    return label;
  } else if (label && status) {
    const statusLabels = (label as Record<string, unknown>)[status];
    if (typeof statusLabels === "string") {
      return statusLabels;
    } else if (statusLabels && typeof statusLabels === "object") {
      const subLabels = statusLabels as Record<string, string>;
      if (caseSubstatus && subLabels[caseSubstatus]) {
        return subLabels[caseSubstatus];
      } else if (subLabels[""]) {
        return subLabels[""];
      }
    }
  }
  return "";
};

export const getConfirmedText = (
  caseSubstatus: ComplianceCaseSubStatus | null | undefined,
  action?: string,
): string => {
  if (action === CaseActions.ReturnToMaker) {
    return "Case returned to maker";
  }
  if (
    ["confirm", "escalate"].includes(action || "") &&
    caseSubstatus === "proposed_fund_escalation"
  ) {
    return "GP escalation confirmed";
  }

  if (caseSubstatus && CONFIRMED_LABELS_MAP.has(caseSubstatus)) {
    return `Case confirmed as ${CONFIRMED_LABELS_MAP.get(caseSubstatus)}`;
  }
  return `Case updated`;
};
